import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import Modal from '../../../../../../components/Modal';
import TextInput from '../../../../../../components/TextInput';
import { formatPrice } from '@/utils/formatter';
import { updateProduct as updateProductMutation } from '../../../../../../graphql/mutations';
import { Sheet } from '../AllergenModal/styled';
import { Field } from '../ProductEditModal/styled';
import Button from '../../../../../../components/Button';
import { HeaderSmall } from '../../../../../../components/Text';
import { productCategories as productCategoriesQuery } from '../../../../../../graphql/queries';
import ExtraGroupFormSection from '../../../../../../components/ExtraGroupFormSection';
import { getProductInput } from '@/utils/data';
import VatSelector from '../../../../../../components/VatSelector';
import i18n from '@/utils/i18n';

function VariantEditModal({ variant, productId, onClose, storeId, vatRates }) {
  const [values, setValues] = useState(variant);
  const [updateProduct, { loading: updatingProduct }] = useMutation(
    updateProductMutation,
  );
  const touchedValues = useMemo(
    () =>
      Object.keys(values).reduce((touched, key) => {
        if (variant[key] !== values[key])
          return { ...touched, [key]: values[key] };
        return touched;
      }, {}),
    [variant, values],
  );

  function handleChangeObject({ target: { type, name, value } }) {
    let formattedValue = value;
    if (type === 'number' || type === 'select-one' || name === 'vat_rate') {
      formattedValue = +value;
    }
    if (name === 'has_cutlery') {
      formattedValue = value === 'null' ? null : value === 'true';
    }
    setValues({ ...values, [name]: formattedValue });
  }

  function handleChange({ target }) {
    const { name, value } = target;
    const type = target.getAttribute('type');
    const stateName = target.getAttribute('data-state-name') || name;
    const formattedValue = type === 'number' ? +value : value;
    setValues({ ...values, [stateName]: formattedValue });
  }

  console.log('variant edit modal', variant);

  async function setAsDefaultVariant() {
    await updateProduct({
      variables: {
        id: productId,
        product: {
          variants: {
            default_variant: variant.variant_id,
          },
        },
      },
      refetchQueries: [
        {
          query: productCategoriesQuery,
          variables: {
            id: storeId,
            filter: 'ALL',
          },
        },
      ],
      update: () => {
        onClose();
      },
    });
  }

  async function handleSave() {
    if (Object.keys(touchedValues).length) {
      const updatedVariant = { name: variant.name, ...touchedValues };

      await updateProduct({
        variables: {
          id: variant.variant_id,
          product: getProductInput(updatedVariant),
        },
        // I have no idea why the automatic apollo store update doesn't work
        // the type of the result of updateVariant is product and the product id is returned
        // just doing a refetchquery for now
        // TODO: fix problem or write own apollo store updater
        refetchQueries: [
          {
            query: productCategoriesQuery,
            variables: {
              id: storeId,
              filter: 'ALL',
            },
          },
        ],
        update: () => {
          onClose();
        },
      });
    }
  }

  return (
    <Modal onClose={onClose}>
      <Sheet>
        <Field>
          <pre>{`${i18n.t('Menu:variant')}: ${variant.variant_id}`}</pre>
        </Field>
        <Field>
          <TextInput
            name="variant-name"
            data-state-name="name"
            value={values.name}
            onChange={handleChange}
            placeholder={i18n.t('Menu:variantName')}
          />
        </Field>
        <Field>
          <TextInput
            placeholder={i18n.t('Menu:variantTitle')}
            name="variant-title"
            data-state-name="title"
            value={values.title || ''}
            onChange={handleChange}
          />
        </Field>
        <p>{i18n.t('Menu:variantTitleInfo')}</p>
        <br />
        <Field>
          <TextInput
            name="price"
            value={values.price}
            onChange={handleChange}
            placeholder={`${i18n.t('Menu:variantPrice')}: ${formatPrice(
              values.price,
            )}`}
            type="number"
          />
        </Field>
        <Field>
          <TextInput
            placeholder={`${i18n.t('Menu:oldPrice')}: ${formatPrice(
              values.old_price,
            )}`}
            name="old_price"
            value={values.old_price}
            onChange={handleChange}
            type="number"
          />
        </Field>

        <VatSelector
          onChange={handleChangeObject}
          vatRate={values.vat_rate}
          vatRates={vatRates}
        />

        <Field>
          <HeaderSmall>
            {i18n.t('Menu:isDefaultVariant', {
              state: variant.is_default
                ? '$t(isDefaultVariantStateIs)'
                : '$t(isDefaultVariantStateIsNot)',
            })}
          </HeaderSmall>
          <Button loading={updatingProduct} onClick={setAsDefaultVariant}>
            {i18n.t('Menu:setAsDefaultVariant')}
          </Button>
        </Field>

        <ExtraGroupFormSection values={values} setValues={setValues} />

        <Button
          disabled={!Object.keys(touchedValues).length}
          loading={updatingProduct}
          onClick={handleSave}
        >
          {i18n.t('Menu:save')}
        </Button>
      </Sheet>
    </Modal>
  );
}

VariantEditModal.propTypes = {
  variant: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  storeId: PropTypes.string.isRequired,
  productId: PropTypes.string.isRequired,
  vatRates: PropTypes.arrayOf(PropTypes.number).isRequired,
};

export default VariantEditModal;
